import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';
import type { CommitSummary, ImportBatch, ImportParse, RevertSummary } from '../lib/types';
import { Banner, Empty, ErrorBox, Field, Loading, Table, Toolbar } from '../ui';

/** What the API returns instead of a result when it refuses. */
interface ApiError {
  error?: string;
}

/** Staged products committed per request — see the slicing note on `runCommit`. */
const SLICE = 500;

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <span>
      <b>{value}</b> <span className="text-muted">{label}</span>
    </span>
  );
}

function Preview({ p }: { p: ImportParse }) {
  return (
    <div className="mt-3">
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
        <span className="text-muted">{p.dryRun ? 'Preview — nothing written' : 'Staged'}</span>
        <Stat label="products" value={p.products} />
        <Stat label="OEs" value={p.oes} />
        <Stat label="cross" value={p.cross} />
        {p.vehicles > 0 && <Stat label="vehicle apps" value={p.vehicles} />}
        {p.media > 0 && <Stat label="images & PDFs" value={p.media} />}
        <Stat label="source rows" value={p.rawRows} />
      </div>

      {p.zip && p.zip.files > 0 && (
        <div className="mb-2 text-[12px]">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span className="text-muted">ZIP</span>
            <Stat label="files" value={p.zip.files} />
            <Stat label="matched a product" value={p.zip.matched} />
            {p.zip.uploaded !== undefined && <Stat label="uploaded to Bunny" value={p.zip.uploaded} />}
          </div>
          {[
            ['No product named like this', p.zip.unmatched],
            ['Same article in several brands — name it in a row', p.zip.ambiguous],
            ['Not a picture or a PDF', p.zip.unsupported],
          ].map(([label, list]) =>
            (list as string[]).length > 0 ? (
              <div key={label as string} className="mt-1 text-[11.5px] text-faint">
                <b>{label as string}:</b> <span className="mono">{(list as string[]).slice(0, 20).join(', ')}</span>
                {(list as string[]).length > 20 && ` … +${(list as string[]).length - 20}`}
              </div>
            ) : null,
          )}
        </div>
      )}

      {p.errors.length > 0 && (
        <div className="mono mb-2 max-h-32 overflow-auto text-[11.5px] text-bad">
          {p.errors.map((e, i) => (
            <div key={i}>
              row {e.row}: {e.message}
            </div>
          ))}
        </div>
      )}

      <Table cols={['Brand', 'Article', 'Name', 'Description', 'OEs', 'Cross', 'Files', 'Attrs']}>
        {p.sample.map((s, i) => (
          <tr key={i}>
            <td className="mono">{s.brand}</td>
            <td className="mono">{s.article}</td>
            <td>{s.name ?? <span className="text-faint">—</span>}</td>
            <td className="text-[11.5px] text-muted">{s.description ?? <span className="text-faint">—</span>}</td>
            <td className="mono text-[11.5px]">{s.oes.join(', ') || <span className="text-faint">—</span>}</td>
            <td className="mono text-[11.5px]">{s.cross.join(', ') || <span className="text-faint">—</span>}</td>
            <td className="text-[11.5px] text-muted">
              {s.images || s.documents ? `${s.images} img · ${s.documents} pdf` : <span className="text-faint">—</span>}
            </td>
            <td className="mono text-[11.5px] text-muted">
              {Object.keys(s.attrs).length ? Object.entries(s.attrs).map(([k, v]) => `${k}=${v}`).join('  ') : '—'}
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export default function ImportProducts() {
  const { request, download } = useApi();
  const fileRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [parse, setParse] = useState<ImportParse | null>(null);
  const [batches, setBatches] = useState<ImportBatch[] | null>(null);
  const [commit, setCommit] = useState<CommitSummary | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  /**
   * Whether this import's name and description replace what is already on the
   * product.
   *
   * Off by default, and the default is the careful one: a catalogue that named a
   * part usually knows it better than a spreadsheet, so an import fills a gap
   * rather than overwriting. But that makes a file whose whole purpose IS the
   * copy — 25 WD-40 descriptions, say — silently do nothing to a product that
   * already has one, and there was no way to say otherwise from here.
   */
  const [overwrite, setOverwrite] = useState(false);

  const loadBatches = useCallback(async () => {
    const r = await request<{ items: ImportBatch[] }>('GET', '/v1/admin/import/batches');
    if (r.data?.items) setBatches(r.data.items);
  }, [request]);

  useEffect(() => {
    void loadBatches();
  }, [loadBatches]);

  async function upload(dry: boolean) {
    setErr('');
    setParse(null);
    setCommit(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setErr('Choose a CSV or XLSX file first.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    const zip = zipRef.current?.files?.[0];
    if (zip) fd.append('zip', zip);
    setBusy(dry ? 'Reading…' : zip ? 'Staging and uploading the ZIP to Bunny…' : 'Staging…');
    const r = await request<ImportParse | ApiError>('POST', `/v1/admin/import/products${dry ? '?dryRun=true' : ''}`, fd);
    setBusy('');
    const d = r.data;
    if (d && 'products' in d) {
      setParse(d);
      if (!dry) void loadBatches();
    } else setErr(d?.error ?? `Import failed (HTTP ${r.status}).`);
  }

  /**
   * Commit in slices. A big catalogue is ~19k products — minutes of work — which no
   * single request should hold open, so the server commits a slice and reports what
   * is left; we loop until it says zero. `commitStaged` is resumable, so a closed
   * tab costs nothing but the remainder.
   */
  async function runCommit(batch: string, pending: number, replace = overwrite) {
    setErr('');
    setCommit(null);
    setProgress({ done: 0, total: pending });
    let done = 0;
    const acc: CommitSummary = {
      processed: 0, committed: 0, failed: 0, oes: 0, cross: 0, media: 0, mediaErrors: [], remaining: 0, errors: [],
    };

    for (;;) {
      setBusy(`Committing ${done}/${pending}…`);
      const r = await request<CommitSummary | ApiError>('POST', '/v1/admin/import/commit', {
        batch,
        limit: SLICE,
        ...(replace ? { overwrite: { name: true, description: true } } : {}),
      });
      const d = r.data;
      if (!d || !('committed' in d)) {
        setErr(d?.error ?? `Commit failed (HTTP ${r.status}).`);
        break;
      }
      const s = d;
      acc.processed += s.processed;
      acc.committed += s.committed;
      acc.failed += s.failed;
      acc.oes += s.oes;
      acc.cross += s.cross;
      acc.media += s.media;
      acc.mediaErrors = [...acc.mediaErrors, ...s.mediaErrors].slice(0, 50);
      acc.remaining = s.remaining;
      acc.errors = [...acc.errors, ...s.errors].slice(0, 50);
      done += s.processed;
      setProgress({ done, total: pending });
      if (s.processed === 0 || s.remaining === 0) break;
    }
    setBusy('');
    setProgress(null);
    setCommit(acc);
    void loadBatches();
  }

  /**
   * Undo a batch, in slices like the commit. Re-importing cannot fix a bad import
   * on its own (a cross edge keyed by relation is added beside the old one, and
   * OEs only ever accumulate), so correcting a catalog means reverting first.
   */
  async function runRevert(batch: string, committed: number) {
    const ok = window.confirm(
      `Remove the ${committed} product(s) this import created?\n\n` +
        'Products we invented are deleted. Products a catalog also carries are kept — only our ' +
        'data comes off them. The batch returns to pending.',
    );
    if (!ok) return;
    setErr('');
    setCommit(null);
    setProgress({ done: 0, total: committed });
    let done = 0;
    for (;;) {
      setBusy(`Reverting ${done}/${committed}…`);
      const r = await request<RevertSummary | ApiError>('POST', '/v1/admin/import/revert', { batch, limit: SLICE });
      const d = r.data;
      if (!d || !('processed' in d)) {
        setErr(d?.error ?? `Revert failed (HTTP ${r.status}).`);
        break;
      }
      done += d.processed;
      setProgress({ done, total: committed });
      if (d.processed === 0 || d.remaining === 0) break;
    }
    setBusy('');
    setProgress(null);
    await loadBatches();
  }

  return (
    <div className="grid gap-3.5" style={{ gridTemplateColumns: '1fr' }}>
      <div className="blk !mb-0">
        <div className="blk-head">
          <h3 className="text-[13px] font-semibold">Import products</h3>
          <span className="text-[11.5px] text-faint">CSV or XLSX → staged → your catalog</span>
        </div>
        <div className="blk-body">
          <Banner icon="↓">
            Fill the template: <b>brand</b> and <b>article</b> are required. <b>oe</b> and <b>cross</b> take several
            values separated by <code>;</code> — a cross is <code>BRAND|ARTICLE</code>. <b>relation</b> defaults to{' '}
            <code>equivalent</code>. <b>images</b> and <b>documents</b> take URLs or names of files in the ZIP; a ZIP
            file named after an article (<code>MY-100_1.jpg</code>) needs no row at all. Any <code>attr:…</code>{' '}
            column becomes a product attribute, labelled under Products → Attributes.
            <button className="btn btn-sm ml-2" onClick={() => download('/v1/admin/import/template', 'product-import-template.xlsx')}>
              Download template
            </button>
          </Banner>

          <div className="flex flex-wrap gap-3">
            <Field label="Product file">
              <input ref={fileRef} className="input" type="file" accept=".csv,.xlsx" onChange={() => setParse(null)} />
            </Field>
            <Field label="Images & PDFs (ZIP, optional)">
              <input ref={zipRef} className="input" type="file" accept=".zip" onChange={() => setParse(null)} />
            </Field>
          </div>

          <Toolbar className="!mb-0 mt-3">
            <button className="btn btn-primary" onClick={() => upload(true)} disabled={!!busy}>
              Preview
            </button>
            <button className="btn" onClick={() => upload(false)} disabled={!!busy}>
              Stage file
            </button>
          </Toolbar>

          {busy && <div className="mt-3"><Loading label={busy} /></div>}
          {err && <div className="mt-3"><ErrorBox>{err}</ErrorBox></div>}
          {parse && <Preview p={parse} />}
        </div>
      </div>

      <div className="blk !mb-0">
        <div className="blk-head">
          <h3 className="text-[13px] font-semibold">Staged imports</h3>
          <span className="text-[11.5px] text-faint">nothing is live until you commit</span>
        </div>
        <div className="blk-body">
          {progress && (
            <div className="mb-3 text-[12px] text-muted">
              Committing {progress.done}/{progress.total} — you can leave; it resumes where it stopped.
            </div>
          )}
          {commit && (
            <div className="mb-3 rounded-md border border-line p-3 text-[12px]">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="text-ok">{commit.committed} committed</span>
                {commit.failed > 0 && <span className="text-bad">{commit.failed} failed</span>}
                <Stat label="OEs" value={commit.oes} />
                <Stat label="cross edges" value={commit.cross} />
                {commit.media > 0 && <Stat label="images & PDFs" value={commit.media} />}
              </div>
              {commit.mediaErrors.length > 0 && (
                <div className="mono mt-2 max-h-32 overflow-auto text-[11.5px] text-warn">
                  {commit.mediaErrors.map((e, i) => (
                    <div key={i}>
                      {e.code}: {e.error}
                    </div>
                  ))}
                </div>
              )}
              {commit.errors.length > 0 && (
                <div className="mono mt-2 max-h-32 overflow-auto text-[11.5px] text-bad">
                  {commit.errors.map((e, i) => (
                    <div key={i}>
                      {e.code}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <label className="mb-2 flex items-center gap-2 text-[12.5px] text-muted">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              disabled={!!busy}
            />
            <span>
              Replace the name and description already on the product
              <span className="text-faint">
                {' '}
                — off, an import only fills what is empty, so a file of new descriptions changes nothing on a
                product that already has one
              </span>
            </span>
          </label>

          {batches === null ? (
            <Loading />
          ) : batches.length === 0 ? (
            <Empty>Nothing staged.</Empty>
          ) : (
            <Table cols={['Batch', 'Products', 'Pending', 'Committed', 'Failed', '']}>
              {batches.map((b) => (
                <tr key={b.batch}>
                  <td className="mono text-[11.5px]">{b.batch}</td>
                  <td>{b.total}</td>
                  <td>{b.pending > 0 ? <b>{b.pending}</b> : <span className="text-faint">0</span>}</td>
                  <td className="text-ok">{b.committed}</td>
                  <td className={b.failed ? 'text-bad' : 'text-faint'}>{b.failed}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={!!busy || b.pending === 0}
                        onClick={() => void runCommit(b.batch, b.pending)}
                      >
                        {b.pending > 0 ? `Commit ${b.pending}` : 'Done'}
                      </button>
                      <button
                        className="btn btn-sm"
                        disabled={!!busy || b.committed === 0}
                        title="Remove the products this import created and return the batch to pending"
                        onClick={() => void runRevert(b.batch, b.committed)}
                      >
                        Revert {b.committed > 0 ? b.committed : ''}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
